import React from 'react';

const cn = (...classes) => classes.filter(Boolean).join(' ');

const SIZE_STYLES = {
    xs: {
        padding: 'px-3 py-1.5',
        text: 'text-xs leading-[1.125rem]',
        gap: 'gap-1.5',
        icon: 'h-3 w-3',
    },
    sm: {
        padding: 'px-3 py-2',
        text: 'text-base leading-5',
        gap: 'gap-2',
        icon: 'h-[0.875rem] w-[0.875rem]',
    },
    md: {
        padding: 'px-5 py-2.5',
        text: 'text-base leading-6',
        gap: 'gap-2',
        icon: 'h-4 w-4',
    },
    lg: {
        padding: 'px-6 py-3',
        text: 'text-lg leading-7',
        gap: 'gap-2.5',
        icon: 'h-[1.125rem] w-[1.125rem]',
    },
};

const SHAPE_STYLES = {
    rounded: 'rounded-lg',
    oval: 'rounded-full',
};

const COLOR_STYLES = {
    blue: {
        base: 'bg-blueNormal text-hardLight border border-transparent',
        hover: 'hover:bg-blueNormalHover',
        active: 'active:bg-blueNormalActive',
        // Match Figma: same blue, white text, reduced overall opacity
        disabled: 'disabled:opacity-40',
        focus: 'focus-visible:ring-blueBrand8',
    },
    // Step circle variation for light mode bounties flow
    stepBlue: {
        // Not included & not active index: secondary text on gray background
        base: 'bg-grayBgNormal text-textPrimary text-base border border-borderUnderline ',
        hover: 'hover:bg-grayBgNormal',
        active: 'active:bg-grayBgNormal',
        disabled: 'disabled:opacity-40',
        focus: 'focus-visible:ring-blueBrand8',
    },
    orange: {
        base: 'bg-orangeNormal text-hardLight border border-transparent',
        hover: 'hover:bg-orangeNormalHover',
        active: 'active:bg-orangeNormalActive',
        disabled: 'disabled:bg-orangeLightActive disabled:text-neutral100',
        focus: 'focus-visible:ring-orangeLightActive',
    },
    danger: {
        base: 'bg-redBrand6 text-neutral50G border border-transparent',
        hover: 'hover:bg-redBrand7',
        active: 'active:bg-redBrand8',
        disabled: 'disabled:bg-redBrand3 disabled:text-neutral100',
        focus: 'focus-visible:ring-redBrand4',
    },
    share: {
        base: 'bg-greenNormal text-neutral50G border border-transparent',
        hover: 'hover:bg-greenNormalHover',
        active: 'active:bg-greenNormalActive',
        disabled: 'disabled:bg-greenLight disabled:text-neutral100',
        focus: 'focus-visible:ring-greenBrand4',
    },
    neutral: {
        base: 'bg-grayBgNormal text-textDefault border border-borderUnderline',
        hover: 'hover:bg-neutral50B',
        active: 'active:bg-neutral50',
        disabled: 'disabled:bg-neutral50 disabled:text-neutral200 disabled:border-neutral50',
        focus: 'focus-visible:ring-neutral50C',
    },
    google: {
        base: 'bg-grayBgNormal text-textPrimary border border-borderUnderline',
        hover: 'hover:bg-neutral50E',
        active: 'active:bg-neutral50C',
        disabled: 'disabled:bg-neutral50 disabled:text-neutral200 disabled:border-neutral50',
        focus: 'focus-visible:ring-neutral50C',
    },
    ghostGray: {
        base: 'bg-primaryBg text-neutral400 border border-transparent',
        hover: 'hover:bg-neutral50E',
        active: 'active:bg-neutral50D',
        disabled: 'disabled:text-neutral200',
        focus: 'focus-visible:ring-neutral50C',
    },
    closeNeutral: {
        base: 'bg-neutral50C text-textPrimary border border-transparent',
        hover: 'hover:bg-neutral50D',
        active: 'active:bg-neutral50E',
        disabled: 'disabled:text-neutral200',
        focus: 'focus-visible:ring-neutral50C',
    },
    ghostBlue: {
        base: 'bg-primaryBg text-blueNormal border border-transparent',
        hover: 'hover:bg-neutral50E',
        active: 'active:bg-neutral50D',
        disabled: 'disabled:text-neutral200',
        focus: 'focus-visible:ring-blueBrand8',
    },
    transparent: {
        base: 'bg-transparent text-textBrandBlue  rounded-none',
        hover: 'hover:text-blueNormalHover ',
        active: 'active:text-textBrandBlue ',
        disabled: 'disabled:text-textSecondary  disabled:opacity-50',
        focus: 'focus-visible:ring-blueBrand8',
    },
};

const ButtonNew = ({
    variant = 'blue',
    size = 'md',
    shape = 'rounded',
    leftIcon,
    rightIcon,
    className = '',
    fullWidth = false,
    fixedMinWidth = false,
    minWidthClass,
    disabled = false,
    type = 'button',
    children,
    ...props
}) => {
    const chosenSize = SIZE_STYLES[size] || SIZE_STYLES.md;
    const chosenShape = SHAPE_STYLES[shape] || SHAPE_STYLES.rounded;
    const chosenVariant = COLOR_STYLES[variant] || COLOR_STYLES.blue;

    const defaultMinWidthClass = 'min-w-[9.0625rem]';
    const resolvedMinWidthClass =
        fixedMinWidth && minWidthClass
            ? minWidthClass
            : fixedMinWidth && !minWidthClass
                ? defaultMinWidthClass
                : "";

    const buttonClasses = cn(
        'inline-flex items-center justify-center font-inter font-medium transition-colors duration-200 ease-in-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-primaryBg',
        'disabled:cursor-not-allowed',
        chosenSize.padding,
        chosenSize.text,
        chosenSize.gap,
        chosenShape,
        chosenVariant.base,
        chosenVariant.hover,
        chosenVariant.active,
        chosenVariant.disabled,
        chosenVariant.focus,
        fullWidth ? 'w-full' : 'w-fit',
        resolvedMinWidthClass,
        className
    );

    const iconClasses = cn('flex items-center justify-center text-inherit', chosenSize.icon);

    return (
        <button type={type} className={buttonClasses} disabled={disabled} {...props}>
            {leftIcon && <span className={iconClasses}>{leftIcon}</span>}
            {children && <span>{children}</span>}
            {rightIcon && <span className={iconClasses}>{rightIcon}</span>}
        </button>
    );
};

export default ButtonNew;

